import { useCallback, useEffect, useRef, useState } from "react";
import { Timer } from "@/components/Timer";
import { useCountdown } from "@/hooks/useCountdown";
import { cancelSpeech } from "@/lib/speech";
import { Volume2 } from "lucide-react";
import { StatusBadge, useItemAudio, ReplayButton, Waveform, AudioFailedPanel } from "@/components/items/SpeakingItem";

export const LineItem = ({ part, item, mode, rate, onSubmit }) => {
  const { phase, play, grace } = useItemAudio(part, item, rate, part.audio);
  const [text, setText] = useState("");
  const textRef = useRef("");
  const doneRef = useRef(false);
  const inputRef = useRef(null);

  const submit = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    cancelSpeech();
    onSubmit({ text: textRef.current });
  }, [onSubmit]);

  useEffect(() => { if (phase === "answer") inputRef.current?.focus(); }, [phase]);
  const left = useCountdown(part.time, phase === "answer", submit);
  const [before, after] = part.key === "F" ? item.sentence.split(/_{2,}/) : ["", ""];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3"><StatusBadge phase={phase} />
          {mode === "practice" && part.audio && <ReplayButton onClick={play} disabled={phase === "listening"} />}
        </div>
        <Timer left={left} total={part.time} />
      </div>
      {part.key === "F" ? (
        <p data-testid="completion-sentence" className="font-display text-xl leading-relaxed text-slate-800">
          {before}<span className="mx-1 inline-block min-w-[6rem] border-b-2 border-indigo-500 px-1 text-center font-mono text-indigo-700">{text || "\u00a0"}</span>{after}
        </p>
      ) : (
        <div data-testid="audio-prompt-indicator" data-playing={phase === "listening"} className={`flex items-center gap-4 rounded-xl p-5 transition-colors ${phase === "listening" ? "bg-indigo-50 ring-2 ring-indigo-300" : "bg-slate-50"}`}>
          <Volume2 className={`h-6 w-6 text-indigo-600 ${phase === "listening" ? "animate-pulse" : "opacity-50"}`} /><Waveform active={phase === "listening"} />
          <span className={`text-sm ${phase === "listening" ? "font-semibold text-indigo-800" : "text-slate-500"}`}>{phase === "listening" ? "Playing prompt… audio plays once" : phase === "failed" ? "Prompt not heard yet" : "Type exactly what you heard"}</span></div>
      )}
      {phase === "failed" && <AudioFailedPanel onReplay={play} grace={grace} />}
      <input ref={inputRef} data-testid="line-response-input" value={text} disabled={phase !== "answer"}
        onChange={(e) => { const v = part.key === "F" ? e.target.value.replace(/\s+/g, "") : e.target.value; textRef.current = v; setText(v); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }} autoComplete="off" spellCheck={false}
        className="w-full rounded-lg border border-slate-300 px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100"
        placeholder={part.key === "F" ? "One word, then Enter" : phase === "answer" ? "Type the sentence, then Enter" : "Wait for the audio…"} />
      <button data-testid="item-submit-btn" onClick={submit} disabled={phase !== "answer"} className="rounded-full bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-40">Submit</button>
    </div>
  );
};
